import React, { Component } from 'react';
import { observer } from 'mobx-react';
import { isEqual } from 'lodash';
import { Box, Button, Select as CoreSelect, Spinner, Suspense } from 'core/components';
import { Field, FormComponent, Select } from 'core/form';
import Model from 'core/model/Model';
import TabResultsTable from 'app/components/detailTabs/TabResultsTable';
import $tabs from 'app/components/detailTabs/$tabs';
import WidgetFrame from '../WidgetFrame';

const FORM_OPTIONS = {
  name: 'Traffic Breakdown Widget Config'
};

const DEFAULT_CONFIG = {
  default_tab_id: 'last'
};

function getTabOptions() {
  return Object.values($tabs.getTabs()).map((tab) => ({ value: tab.id, label: tab.title }));
}

function resolveTabId(config) {
  if (config.tab_id) {
    return config.tab_id;
  }
  if (config.default_tab_id && config.default_tab_id !== 'last') {
    return config.default_tab_id;
  }
  return null;
}

@observer
class TrafficBreakdownWidget extends Component {
  static fields = {
    default_tab_id: {
      label: 'Default Traffic Breakdown Dimension',
      options: [],
      defaultValue: 'last',
      rules: 'required',
      getFieldProps(config, { prefix = '' } = {}) {
        const options = [{ value: 'last', label: 'Last Selected' }, ...getTabOptions()];
        const onChange = (field, value, prev, form) => {
          if (value !== 'last') {
            form.setValue(`${prefix && `${prefix}.`}tab_id`, value);
          }
        };
        return { options, onChange };
      }
    },
    tab_id: {
      hidden: true
    }
  };

  static getDefaultTitle(config) {
    const tab = config?.tab_id ? Object.values($tabs.getTabs()).find((t) => t.id === config.tab_id) : null;
    return tab ? `Traffic By ${tab.title}` : 'Traffic Breakdown';
  }

  state = {
    isConfigurePanelOpen: false,
    config: null,
    loading: true
  };

  static getDerivedStateFromProps(props, state) {
    const { config } = props;

    if (!state.config) {
      if (!config) {
        return { config: DEFAULT_CONFIG, isConfigurePanelOpen: true, model: new Model(DEFAULT_CONFIG) };
      }
      const configWithDefaults = { ...DEFAULT_CONFIG, ...config };
      return {
        config: configWithDefaults,
        selectedTabId: resolveTabId(configWithDefaults),
        model: new Model(configWithDefaults)
      };
    }

    if (config && !isEqual(config, state.config)) {
      return { config, selectedTabId: resolveTabId(config), model: new Model(config) };
    }

    return null;
  }

  componentDidMount() {
    const { config } = this.state;

    const tabs = Object.values($tabs.getTabs());
    const tabOptions = tabs.map((tab) => ({ value: tab.id, label: tab.title }));
    this.setState({ tabOptions });

    if (config.default_tab_id) {
      if (config.default_tab_id === 'last') {
        this.setState({ loading: false });
      } else {
        this.setState({ loading: false });
      }
    } else {
      this.setState({ loading: false });
    }
  }

  get config() {
    const { config } = this.state;
    return { ...DEFAULT_CONFIG, ...config };
  }

  get tab() {
    const { selectedTabId } = this.state;
    if (!selectedTabId) {
      return null;
    }
    return Object.values($tabs.getTabs()).find((tab) => tab.id === selectedTabId) ?? null;
  }

  handleSave = (form) => {
    const { onConfigChange } = this.props;
    const { model } = this.state;
    const { config } = this;

    let newConfig;
    const { default_tab_id } = form.getValues();
    model.set('default_tab_id', default_tab_id);
    if (default_tab_id === 'last') {
      newConfig = { ...config, default_tab_id };
      this.setState({ isConfigurePanelOpen: false, config: newConfig });
    } else {
      model.set('tab_id', default_tab_id);
      newConfig = { tab_id: default_tab_id, default_tab_id };
      this.setState({ isConfigurePanelOpen: false, config: newConfig });
    }

    if (onConfigChange) {
      onConfigChange(newConfig);
    }
  };

  handleCancel = (form) => {
    form.reset();
    this.setState({ isConfigurePanelOpen: false });
  };

  handleShowConfigurePanel = () => {
    this.setState({ isConfigurePanelOpen: true });
  };

  renderConfigurePanel() {
    const { model, config } = this.state;
    const { fields } = TrafficBreakdownWidget;

    return (
      <FormComponent fields={fields} options={FORM_OPTIONS} model={model}>
        {({ form }) => (
          <Box p={2}>
            <Box pt={2}>
              <Field name="default_tab_id" large {...fields.default_tab_id.getFieldProps(form.getValues())}>
                <Select />
              </Field>
            </Box>
            {config.default_tab_id && (
              <Button text="Cancel" onClick={() => this.handleCancel(form)} mr={1} minWidth={100} small />
            )}
            <Button
              text="Save"
              intent="primary"
              disabled={!form.valid}
              onClick={() => this.handleSave(form)}
              minWidth={100}
              small
            />
          </Box>
        )}
      </FormComponent>
    );
  }

  handleTabChange = (tab_id) => {
    const { onConfigChange } = this.props;
    const { model } = this.state;
    const { config } = this;

    model.set('tab_id', tab_id);
    const newConfig = { ...config, tab_id };
    this.setState({ selectedTabId: tab_id });

    if (onConfigChange && config.default_tab_id === 'last') {
      onConfigChange(newConfig);
    }
  };

  renderData() {
    const { tabOptions } = this.state;
    const { tab } = this;

    const queryOverrides = {
      lookback_seconds: 86400,
      aggregateType: 'p95th_bits_per_sec'
    };

    return (
      <>
        <Box p={1}>
          <CoreSelect options={tabOptions} values={tab?.id} onChange={this.handleTabChange} />
        </Box>
        {tab && <TabResultsTable queryOverrides={queryOverrides} useCard={false} {...tab} />}
      </>
    );
  }

  render() {
    const { onRemove, canCustomize, title, ...props } = this.props;
    const { isConfigurePanelOpen, loading } = this.state;
    const { config } = this;

    return (
      <WidgetFrame
        canCustomize={canCustomize}
        configAction={this.handleShowConfigurePanel}
        onRemove={onRemove}
        title={title || TrafficBreakdownWidget.getDefaultTitle(config)}
        display="flex"
        flexDirection="column"
        {...props}
      >
        <Suspense loading={loading} fallback={<Spinner size={24} />}>
          {isConfigurePanelOpen && this.renderConfigurePanel()}
          {!isConfigurePanelOpen && this.renderData()}
        </Suspense>
      </WidgetFrame>
    );
  }
}

export default TrafficBreakdownWidget;
